/**
 * @fileoverview Dialog - Accessible modal dialog components
 *
 * Purpose: Provides shadcn/ui modal dialogs with full Radix UI accessibility
 * Dependencies: Radix UI Dialog primitives, lucide-react icons
 * Used by: Confirmations, forms, detail views, modals, image viewers throughout the app
 *
 * Components exported:
 * - Dialog: Root container
 * - DialogTrigger: Button that opens the dialog
 * - DialogPortal: Portal for rendering dialog outside DOM hierarchy
 * - DialogOverlay: Semi-transparent backdrop
 * - DialogClose: Close button component
 * - DialogContent: Main dialog content with backdrop and close button
 * - DialogHeader: Header section for title and description
 * - DialogFooter: Footer section for actions/buttons
 * - DialogTitle: Accessible title (required for a11y)
 * - DialogDescription: Accessible description
 *
 * @example
 * <Dialog>
 *   <DialogTrigger asChild>
 *     <Button>Open Dialog</Button>
 *   </DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Are you absolutely sure?</DialogTitle>
 *       <DialogDescription>
 *         This action cannot be undone. This will permanently delete your account.
 *       </DialogDescription>
 *     </DialogHeader>
 *     <DialogFooter>
 *       <Button variant="outline" onClick={handleClose}>Cancel</Button>
 *       <Button variant="destructive" onClick={handleConfirm}>Confirm</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 */

"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/utils/misc";
import { PopoverPortalContainerProvider } from "@/ui/composed/overlays/popover";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/**
 * DialogOverlay provides the semi-transparent backdrop
 *
 * Features:
 * - Covers entire viewport with semi-transparent overlay
 * - Smooth fade-in/fade-out animations
 * - Prevents interaction with content below
 * - Click to close (when used with DialogContent)
 *
 * @component
 */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * DialogContent displays the main modal content
 *
 * Features:
 * - Centered modal with backdrop
 * - Focus trap (keeps focus within dialog)
 * - ESC key to close
 * - Click outside backdrop to close
 * - Automatic close button in top-right corner
 * - Accessible (ARIA attributes, screen reader support)
 * - Smooth zoom and slide animations
 * - Responsive (adapts to mobile screens)
 *
 * @component
 * @example
 * <DialogContent>
 *   <DialogHeader>
 *     <DialogTitle>Confirmation</DialogTitle>
 *     <DialogDescription>Are you sure?</DialogDescription>
 *   </DialogHeader>
 *   <p>Dialog body content goes here</p>
 *   <DialogFooter>
 *     <Button>Confirm</Button>
 *   </DialogFooter>
 * </DialogContent>
 */
interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /**
   * When true, suppresses the default close (X) button in the top-right
   * corner. Used by surfaces (e.g. command palettes) that prefer
   * click-outside / Escape as the only dismiss affordances.
   */
  hideClose?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose, ...props }, ref) => {
  // Publish the content node so popovers inside the dialog portal *into* it
  // and stay within the modal scroll-lock subtree (keeps long combobox lists
  // wheel-scrollable — react-remove-scroll otherwise swallows the wheel).
  const [contentEl, setContentEl] = React.useState<HTMLDivElement | null>(null);
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      setContentEl(node);
      if (typeof ref === "function") ref(node);
      else if (ref)
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRefs}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className,
        )}
        {...props}
      >
        <PopoverPortalContainerProvider value={contentEl}>
          {children}
        </PopoverPortalContainerProvider>
        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * DialogHeader is a layout wrapper for the dialog title and description
 *
 * Features:
 * - Consistent spacing between title and description
 * - Responsive text alignment (center on mobile, left on desktop)
 * - Semantic structure for accessibility
 *
 * @component
 * @example
 * <DialogHeader>
 *   <DialogTitle>Delete Account</DialogTitle>
 *   <DialogDescription>This action is permanent</DialogDescription>
 * </DialogHeader>
 */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

/**
 * DialogFooter is a layout wrapper for action buttons
 *
 * Features:
 * - Responsive layout (stacked on mobile, row on desktop)
 * - Right-aligned buttons on desktop
 * - Consistent spacing between buttons
 * - Reverse order on mobile (primary button at bottom)
 *
 * @component
 * @example
 * <DialogFooter>
 *   <Button variant="outline">Cancel</Button>
 *   <Button variant="destructive">Delete</Button>
 * </DialogFooter>
 */
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
